const STATUS_META = {
  draft: { label: "Draft", cls: "border-zinc-400 text-zinc-400" },
  sent: { label: "Sent", cls: "border-purple-600 text-purple-700" },
  paid: { label: "Paid", cls: "border-emerald-600 text-emerald-700" },
  estimate: { label: "Estimate", cls: "border-amber-500 text-amber-600" },
};

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "—";

const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PAYMENT_METHOD_LABELS = {
  online: "Pay online (card / electronic)",
  refund: "Deducted from tax refund",
  venmo: "Venmo",
};

export default function InvoiceSheet({ invoice }) {
  const meta = STATUS_META[invoice.status] || STATUS_META.draft;
  const isEstimate = invoice.status === "estimate";
  const range = (lo, hi) => (hi > lo ? `${money(lo)} – ${money(hi)}` : money(hi));
  return (
    <div
      id="invoice-sheet"
      data-testid="invoice-sheet"
      className="relative min-w-0 overflow-hidden bg-white p-5 text-left text-zinc-900 shadow-[0_24px_60px_rgba(0,0,0,0.5)] sm:p-12 [overflow-wrap:anywhere]"
    >
      <div className="absolute inset-x-0 top-0 h-1.5 bg-purple-600" />
      <div
        data-testid="invoice-sheet-status"
        className={`pointer-events-none mb-4 inline-block rotate-3 border-2 px-3 py-1 font-mono text-xs font-bold uppercase tracking-[0.3em] ${meta.cls}`}
      >
        {invoice.kind === "mobile" && invoice.status === "draft" ? "Unpaid · Admin only" : meta.label}
      </div>
      <div>
        <p className="font-display text-xl font-semibold tracking-tight">STEELE INDUSTRIES</p>
        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          Tax Preparation &amp; Accounting
        </p>
      </div>
      <div className="mt-10 flex flex-wrap justify-between gap-6">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">{isEstimate ? "Estimate" : "Invoice"}</p>
          <p data-testid="invoice-sheet-number" className="mt-1 font-mono text-lg font-semibold text-purple-700">
            {invoice.number}
          </p>
          <p className="mt-1 text-xs text-zinc-500">Issued {fmtDate(invoice.created_at)}</p>
        </div>
        <div className="min-w-0 max-w-full sm:text-right">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Billed to</p>
          <p className="mt-1 text-sm font-semibold">{invoice.recipient_name}</p>
          <p className="text-xs text-zinc-500">{invoice.recipient_email}</p>
        </div>
      </div>
      <table className="mt-10 w-full table-fixed text-sm">
        <thead>
          <tr className="border-b-2 border-zinc-900 text-left font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            <th className="py-2 pr-4 font-medium">Form / Service</th>
            <th className="w-[40%] py-2 text-right font-medium">{isEstimate ? "Estimated range" : "Amount"}</th>
          </tr>
        </thead>
        <tbody>
          {invoice.lines.map((l, i) => (
            <tr key={i} data-testid={`invoice-sheet-line-${i}`} className="border-b border-zinc-200">
              <td className="py-3 pr-4 text-zinc-800">{l.label}</td>
              <td className="py-3 text-right font-mono text-zinc-800">
                {isEstimate ? range(Number(l.price_min || 0), Number(l.price || 0)) : money(l.price)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-6 flex justify-end">
        <div className="w-56">
          <div className="flex items-baseline justify-between border-t-2 border-zinc-900 pt-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              {isEstimate ? "Estimated total" : invoice.status === "paid" ? "Total paid" : "Total due"}
            </span>
            <span data-testid="invoice-sheet-total" className="font-display text-2xl font-semibold text-purple-700">
              {isEstimate
                ? range(Number(invoice.estimate_min || 0), Number(invoice.estimate_max || invoice.total))
                : money(invoice.total)}
            </span>
          </div>
        </div>
      </div>
      {isEstimate && (
        <p className="mt-6 border border-amber-300 bg-amber-50 p-4 text-xs leading-relaxed text-amber-800" data-testid="invoice-sheet-estimate-note">
          This is an estimate, not a bill — nothing is due right now. Your final fee is confirmed before any work
          begins.
        </p>
      )}
      {invoice.notes && (
        <p className="mt-8 border-t border-zinc-200 pt-4 text-xs leading-relaxed text-zinc-500">{invoice.notes}</p>
      )}
      {invoice.signature_data && (
        <div className="mt-10 border-t border-zinc-200 pt-6" data-testid="invoice-signature-block">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Authorized signature</p>
              <img
                src={invoice.signature_data}
                alt={`Signature of ${invoice.signer_name}`}
                data-testid="invoice-signature-img"
                className="mt-2 h-auto max-h-20 w-auto max-w-full object-contain"
              />
              <p className="mt-1 border-t border-zinc-300 pt-1 text-sm font-medium text-zinc-800">
                {invoice.signer_name}
              </p>
              <p className="text-xs text-zinc-500">Signed {fmtDate(invoice.signed_at)}</p>
            </div>
            <div className="text-right">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                Client agreed to pay via
              </p>
              <p data-testid="invoice-payment-method" className="mt-1 text-sm font-semibold text-purple-700">
                {PAYMENT_METHOD_LABELS[invoice.payment_method] || invoice.payment_method}
              </p>
            </div>
          </div>
        </div>
      )}
      <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
        No mystery. No refund games. — Steele Industries
      </p>
    </div>
  );
}
